/**
 * DeviceStateTimeline (DE-137) — horizontal Gantt/swim-lane chart of
 * system status over time. Three rows, one per state axis, plus an
 * error-tick strip:
 *
 *   sys_state    — derived from `state_change` events (data.state)
 *   demo_mode    — derived from `demo_mode_change` events (data.demo_mode)
 *   proj_source  — derived from `source_dwell` events (data.to)
 *   (markers)    — `error` and `boot` events as vertical ticks
 *
 * Bands are computed client-side from the event stream: each transition
 * starts a band for the new value that runs until the next transition on
 * the same axis (or the window end). When the first transition on an axis
 * is interior to the window, we draw an "unknown" placeholder band from
 * `since` to that first transition so it's visually obvious we don't have
 * the entry value.
 *
 * The time-range selector mirrors DeviceTelemetryChart's (6h/24h/7d/30d/
 * 90d) so operators can cross-correlate the two views by eye.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDeviceStateHistory, type StateHistoryEvent } from '../api/hooks';
import {
  canonSource,
  DEMO_MODE_LABEL,
  PROJ_SOURCE_LABEL,
  SYS_STATE_LABEL,
} from './ucEnums';

type RangeKey = '1h' | '2h' | '6h' | '24h' | '7d' | '30d' | '90d';

const RANGES: Array<{ key: RangeKey; hours: number; label: string }> = [
  { key: '1h', hours: 1, label: '1 hour' },
  { key: '2h', hours: 2, label: '2 hours' },
  { key: '6h', hours: 6, label: '6 hours' },
  { key: '24h', hours: 24, label: '24 hours' },
  { key: '7d', hours: 168, label: '7 days' },
  { key: '30d', hours: 720, label: '30 days' },
  { key: '90d', hours: 2160, label: '90 days' },
];

export type Dimension = 'sys_state' | 'demo_mode' | 'proj_source';

export type BandValue = number | string | null;

export interface Band {
  start: number;
  end: number;
  value: BandValue;
  label: string;
}

// Per-axis value → swatch. sys_state and demo_mode have small, fixed
// enumerated ranges so we index directly. proj_source values are sparse
// hex codes (0x10/0x30/0x80/…) — pick a colour deterministically from a
// 10-step palette using `value % palette.length`.
const SYS_STATE_COLOR: Record<number, string> = {
  0: '#94a3b8', // Standby — slate
  1: '#fbbf24', // Warming — amber
  2: '#22c55e', // LampOn — green (operationally "running")
  3: '#0ea5e9', // Cooling — sky
  4: '#6366f1', // Zoom — indigo
  5: '#eab308', // Pause — yellow
  6: '#06b6d4', // Freeze — cyan
  7: '#a855f7', // AudioOnly — purple
  8: '#f43f5e', // AudioOnlyCooling — rose
};
const DEMO_MODE_COLOR: Record<number, string> = {
  0: '#9ca3af', // unsupported — gray
  1: '#cbd5e1', // supported (idle) — slate-light
  2: '#10b981', // active — emerald
  3: '#f59e0b', // suspended — amber
};
const PROJ_SOURCE_PALETTE = [
  '#0ea5e9', '#06b6d4', '#14b8a6', '#10b981', '#84cc16',
  '#eab308', '#f97316', '#ec4899', '#d946ef', '#8b5cf6',
];

const UNKNOWN_COLOR = '#e5e7eb';

// Tiny FNV-1a hash so string source labels ("HDMI3", "USB1", …) get a
// stable colour index into PROJ_SOURCE_PALETTE. Sticking with a hash
// (rather than maintaining a string→colour table) means newly-supported
// sources don't need a code change to render.
function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h;
}

export function colorFor(dim: Dimension, value: BandValue): string {
  if (value === null || value === undefined) return UNKNOWN_COLOR;
  if (dim === 'sys_state' && typeof value === 'number') {
    return SYS_STATE_COLOR[value] ?? UNKNOWN_COLOR;
  }
  if (dim === 'demo_mode' && typeof value === 'number') {
    return DEMO_MODE_COLOR[value] ?? UNKNOWN_COLOR;
  }
  // proj_source: events may carry numeric (legacy) or string label (current).
  const idx = typeof value === 'number'
    ? Math.abs(value)
    : hashStr(value);
  return PROJ_SOURCE_PALETTE[idx % PROJ_SOURCE_PALETTE.length]!;
}

export function labelFor(dim: Dimension, value: BandValue): string {
  if (value === null || value === undefined) return 'unknown';
  if (dim === 'sys_state' && typeof value === 'number') {
    return SYS_STATE_LABEL[value] ?? `state ${value}`;
  }
  if (dim === 'demo_mode' && typeof value === 'number') {
    return DEMO_MODE_LABEL[value] ?? `mode ${value}`;
  }
  // proj_source — events usually carry the human label string verbatim.
  if (typeof value === 'string') return value;
  return PROJ_SOURCE_LABEL[value] ?? `source 0x${value.toString(16)}`;
}

export function valueFor(ev: StateHistoryEvent, dim: Dimension): BandValue {
  const d = ev.data;
  if (dim === 'sys_state' && ev.event_type === 'state_change') {
    const v = d.state;
    return typeof v === 'number' ? v : null;
  }
  if (dim === 'demo_mode' && ev.event_type === 'demo_mode_change') {
    const v = d.demo_mode;
    return typeof v === 'number' ? v : null;
  }
  if (dim === 'proj_source' && ev.event_type === 'source_dwell') {
    // Canonicalise to a single string identity so a source that arrives as a
    // numeric code (legacy rows) and as a string label dedupe to one band /
    // colour / legend entry. See canonSource() in ucEnums.
    return canonSource(d.to as number | string | null | undefined);
  }
  return null;
}

interface MarkerCluster {
  centroidMs: number;
  events: StateHistoryEvent[];
}

// Linear-time clustering: walk markers in ts order and merge into the
// current cluster while their distance to the centroid is below the
// pixel-threshold's time equivalent. When widthPx is 0 (pre-measure)
// we return one cluster per event (no merging) so the chart still
// renders the right number of ticks on first paint.
function clusterMarkers(
  markers: StateHistoryEvent[],
  spanMs: number,
  widthPx: number,
): MarkerCluster[] {
  if (markers.length === 0) return [];
  const sorted = markers
    .slice()
    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
  const thresholdMs =
    widthPx > 0 ? (3 / widthPx) * spanMs : 0; // 3 px equivalent in ms
  const clusters: MarkerCluster[] = [];
  for (const ev of sorted) {
    const t = new Date(ev.ts).getTime();
    const last = clusters[clusters.length - 1];
    if (last && Math.abs(t - last.centroidMs) <= thresholdMs) {
      last.events.push(ev);
      // Mean-centroid keeps the cluster anchor stable as more events join.
      last.centroidMs =
        last.events.reduce(
          (sum, e) => sum + new Date(e.ts).getTime(),
          0,
        ) / last.events.length;
    } else {
      clusters.push({ centroidMs: t, events: [ev] });
    }
  }
  return clusters;
}

// Subtracts every band where `predicate` holds from the input bands,
// splitting them as needed. Used to hide Demo / Source rows during
// sys_state == Standby — the device keeps publishing stale demo and
// source values while the projector is off, but those values are not
// meaningful to the operator and visually crowd the chart.
export function maskBandsAgainst(
  bands: Band[],
  maskBands: Band[],
  predicate: (b: Band) => boolean,
): Band[] {
  const masks = maskBands.filter(predicate);
  if (masks.length === 0) return bands;
  const out: Band[] = [];
  for (const b of bands) {
    let segs: Array<{ start: number; end: number }> = [{ start: b.start, end: b.end }];
    for (const m of masks) {
      const next: typeof segs = [];
      for (const s of segs) {
        if (m.end <= s.start || m.start >= s.end) {
          next.push(s);
          continue;
        }
        if (m.start > s.start) next.push({ start: s.start, end: Math.min(m.start, s.end) });
        if (m.end < s.end) next.push({ start: Math.max(m.end, s.start), end: s.end });
      }
      segs = next;
      if (segs.length === 0) break;
    }
    for (const s of segs) {
      if (s.end > s.start) out.push({ ...b, start: s.start, end: s.end });
    }
  }
  return out;
}

// DE-160. Span = one contiguous offline interval (between an MQTT
// `disconnected` lifecycle event and the next `connected`). Used to
// render a hatched overlay across the whole chart height so the operator
// can see at a glance that the underlying band colours are stale during
// that period (lanes keep their last-known value because the device
// can't publish updates while it's offline).
export interface OfflineSpan {
  start: number;
  end: number;
  // Originating disconnect's ts, or null when the span starts at
  // sinceMs because the disconnect predates the window.
  disconnectMs: number | null;
  reason: string | null;
}

// 45° diagonal stripes, slate-500 at ~45% on a near-transparent slate
// wash. The 6 px period keeps the texture readable at every zoom level
// without obscuring band colours or marker ticks beneath.
const OFFLINE_HATCH_BG =
  'repeating-linear-gradient(45deg, rgba(100,116,139,0.45) 0 1.5px, transparent 1.5px 6px), rgba(100,116,139,0.08)';

export function buildOfflineSpans(
  events: StateHistoryEvent[],
  sinceMs: number,
  untilMs: number,
): OfflineSpan[] {
  // events is the ts-ASC merge of (anchors before sinceMs) + (window
  // events). Anchors give us the device's presence state at sinceMs so
  // the leading edge of the window paints correctly when the most recent
  // disc/conn predates the window.
  const spans: OfflineSpan[] = [];
  let openStart: number | null = null;
  let openDisconnect: number | null = null;
  let openReason: string | null = null;
  for (const ev of events) {
    if (ev.event_type !== 'presence') continue;
    const tMs = new Date(ev.ts).getTime();
    const kind = (ev.data as { kind?: unknown }).kind;
    if (kind === 'disconnected') {
      if (openStart === null) {
        // Anchor disc (tMs < sinceMs): span starts at the window edge.
        // Window disc:                   span starts at the disc ts.
        openStart = Math.max(tMs, sinceMs);
        openDisconnect = tMs;
        const reason = (ev.data as { reason?: unknown }).reason;
        openReason = typeof reason === 'string' && reason.length > 0 ? reason : null;
      }
      // Repeated disconnects with no intervening connect: keep the
      // earliest anchor — AWS occasionally double-publishes lifecycle.
    } else if (kind === 'connected') {
      if (openStart !== null) {
        if (tMs > openStart) {
          spans.push({
            start: openStart,
            end: Math.min(tMs, untilMs),
            disconnectMs: openDisconnect,
            reason: openReason,
          });
        }
        openStart = null;
        openDisconnect = null;
        openReason = null;
      }
      // No open span + `connected` = device was already online; nothing
      // to emit (anchor and window both agree).
    }
  }
  // Trailing open span — device disconnected and never reconnected
  // within the window. Clamp to the right edge.
  if (openStart !== null) {
    spans.push({
      start: openStart,
      end: untilMs,
      disconnectMs: openDisconnect,
      reason: openReason,
    });
  }
  return spans;
}

export function buildBands(
  events: StateHistoryEvent[],
  dim: Dimension,
  sinceMs: number,
  untilMs: number,
): Band[] {
  // `events` is the ts-ASC merge of (anchors strictly before sinceMs) +
  // (window events). We fold two kinds of point into one ordered timeline:
  //   - value transitions for this dim (state_change / demo_mode_change /
  //     source_dwell), value = the new lane value;
  //   - `boot` events, modelled as a reset to `null` (unknown). The uC does
  //     not re-emit state on reboot (the gateway pulls full state into the
  //     shadow, not into events), so a pre-reboot value must NOT carry across
  //     the boot — cut the band and run "unknown" until the next real event.
  //     The trailing "unknown" is later filled from the live shadow value by
  //     reconcileWithLive when the device is online.
  const pts: Array<{ ts: number; value: BandValue; boot: boolean }> = [];
  for (const ev of events) {
    if (ev.event_type === 'boot') {
      pts.push({ ts: new Date(ev.ts).getTime(), value: null, boot: true });
      continue;
    }
    const v = valueFor(ev, dim);
    if (v === null) continue;
    pts.push({ ts: new Date(ev.ts).getTime(), value: v, boot: false });
  }
  if (pts.length === 0) {
    return [{ start: sinceMs, end: untilMs, value: null, label: labelFor(dim, null) }];
  }
  // Sort by ts; at the SAME ts the boot reset must come BEFORE value transitions
  // so a post-boot state recorded at the boot instant (the dispatcher's boot-sync
  // synthesizes state_change/demo_mode_change/source_dwell AT the boot ts) isn't
  // blanked to "unknown" by the reset. Without this tiebreak the order is
  // whatever the DB returned and the post-boot Standby could vanish.
  pts.sort((a, b) => a.ts - b.ts || Number(b.boot) - Number(a.boot));
  // Collapse everything at/before sinceMs into the value active at the left
  // edge. A pre-window boot that post-dates the last value transition leaves
  // the leading value null (the anchor is invalidated by the reboot).
  let idx = 0;
  let curValue: BandValue = null;
  while (idx < pts.length && pts[idx]!.ts <= sinceMs) {
    curValue = pts[idx]!.value;
    idx++;
  }
  const bands: Band[] = [];
  let curStart = sinceMs;
  for (; idx < pts.length; idx++) {
    const p = pts[idx]!;
    if (p.ts >= untilMs) break;
    if (p.value === curValue) continue; // no visible change (incl. boot==boot)
    if (p.ts > curStart) {
      bands.push({ start: curStart, end: p.ts, value: curValue, label: labelFor(dim, curValue) });
    }
    curStart = p.ts;
    curValue = p.value;
  }
  if (curStart < untilMs) {
    bands.push({ start: curStart, end: untilMs, value: curValue, label: labelFor(dim, curValue) });
  }
  return bands;
}

// Reconcile the trailing band against the device's live shadow value. Produces
// normal bands (no special styling) — the operator sees the device's current
// value at the right edge even when we never received the transition event.
// Skipped when offline (the shadow may itself be stale). Pure (the live shadow
// is passed in, not closed over) so it's unit-testable.
export function reconcileWithLive(
  bands: Band[],
  dim: Dimension,
  liveValue: BandValue | null,
  liveAnchor: LiveAnchor | undefined,
  nowMs: number,
): Band[] {
  if (!liveAnchor || !liveAnchor.online) return bands;
  if (liveValue === null || liveValue === undefined) return bands;
  if (bands.length === 0) return bands;
  const last = bands[bands.length - 1]!;
  if (last.value === liveValue) return bands;
  // Only apply to a LIVE window. The trailing band ends at the window's right
  // edge; for a live window that edge IS the current minute (nowMs), for a
  // panned-back / historical window it's earlier — and the current shadow value
  // doesn't describe the past. Compare against nowMs, NOT lastSeen: the window
  // edge is minute-floored while lastSeen is precise, so lastSeen can sit a few
  // seconds *past* last.end on a live window (the old `lastSeen >= last.end`
  // test wrongly skipped the fill for every actively-publishing device).
  if (last.end < nowMs) return bands;
  if (last.value === null) {
    // No events on this lane in the trailing region. All three lanes are
    // emit-on-change and boots reset the lane (see buildBands), so "no events
    // since the last boot / window start" means the value never changed — the
    // device has held the shadow's current value across the whole band. Paint
    // it (covers empty lanes + the post-boot quiet case).
    return [
      ...bands.slice(0, -1),
      { ...last, value: liveValue, label: labelFor(dim, liveValue) },
    ];
  }
  // Known trailing value differs from the shadow: a transition happened but we
  // have no event timestamp for it. Confine the repaint to [max(lastSeen,
  // start), end] (clamped to the band) so we don't rewrite history before the
  // device's last heartbeat.
  const lastSeen = liveAnchor.lastSeen;
  const boundary =
    lastSeen === null ? last.start : Math.min(Math.max(lastSeen, last.start), last.end);
  const out = bands.slice(0, -1);
  if (boundary <= last.start) {
    // Whole trailing band is within the fresh window → replace it.
    out.push({ ...last, value: liveValue, label: labelFor(dim, liveValue) });
  } else if (boundary >= last.end) {
    // lastSeen at/after the minute-floored right edge → nothing to repaint yet.
    out.push(last);
  } else {
    out.push({ ...last, end: boundary });
    out.push({ start: boundary, end: last.end, value: liveValue, label: labelFor(dim, liveValue) });
  }
  return out;
}

// Append a synthetic trailing offline span when the `online` pill reads offline
// (stale last_seen) but no presence disconnect already covers the right edge —
// keeps the timeline in step with the pill instead of waiting for the presence
// scrubber (scrub_stale_presence in schema.sql). Pure (testable).
export function withSyntheticOffline(
  spans: OfflineSpan[],
  live: { online: boolean; lastSeen: number | null },
  sinceMs: number,
  untilMs: number,
): OfflineSpan[] {
  if (live.online || live.lastSeen === null || live.lastSeen >= untilMs) return spans;
  if (spans.some((s) => s.end >= untilMs)) return spans;
  return [
    ...spans,
    {
      start: Math.max(live.lastSeen, sinceMs),
      end: untilMs,
      disconnectMs: live.lastSeen,
      reason: 'no recent data',
    },
  ];
}

// Compact "how far back is the window from now" indicator for the slide
// controls. Renders e.g. "30 min ago" / "3 h ago" / "2 d ago".
function fmtOffsetAgo(offsetMs: number): string {
  const min = Math.round(offsetMs / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h`;
  const d = Math.round(h / 24);
  return `${d} d`;
}

// Header-edge formatter: weekday + day + month + HH:mm, plus year when the
// window is wide enough that the user might lose track (>30 d). Always
// shows weekday so the operator can anchor "what day is this?" at a glance.
function fmtWindowEdge(ms: number, hours: number): string {
  const d = new Date(ms);
  return d.toLocaleString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    ...(hours > 720 ? { year: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Browser <input type="datetime-local"> wants `YYYY-MM-DDTHH:mm` in *local*
// time (no Z, no seconds). toISOString() would give UTC, so format manually.
function toLocalDatetimeInputValue(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtAxisTick(ms: number, hours: number): string {
  const d = new Date(ms);
  if (hours <= 36) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (hours <= 24 * 14) {
    return d.toLocaleDateString([], { weekday: 'short', day: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: '2-digit' });
}

const ROW_HEIGHT_EXPANDED = 22;
const ROW_HEIGHT_COLLAPSED = 4;
const ROW_GAP = 6;
const ROW_LABELS: Array<{ dim: Dimension; label: string }> = [
  { dim: 'sys_state', label: 'System' },
  { dim: 'demo_mode', label: 'Demo' },
  { dim: 'proj_source', label: 'Source' },
];

const DEFAULT_RANGE: RangeKey = '24h';
const MIN_CUSTOM_SPAN_MS = 5 * 60 * 1000; // 5 min floor on brush / band-click

function spanForKey(key: RangeKey): number {
  return RANGES.find((r) => r.key === key)!.hours * 3600 * 1000;
}

// Find the preset whose span is closest to `spanMs`. Used by `Reset` to
// snap a brushed window back to the nearest preset rather than throwing
// the user back to the default.
function nearestPresetTo(spanMs: number): RangeKey {
  let best: RangeKey = DEFAULT_RANGE;
  let bestDist = Infinity;
  for (const r of RANGES) {
    const d = Math.abs(r.hours * 3600 * 1000 - spanMs);
    if (d < bestDist) {
      bestDist = d;
      best = r.key;
    }
  }
  return best;
}

/**
 * URL-backed window state. Encodes:
 *   ?range=<key>   — preset key; omitted when key is the default (24h)
 *   ?span=<ms>     — custom (brushed) span in ms; replaces `range`
 *   ?until=<ISO>   — window right edge; omitted when isLive
 *
 * Both reads and writes use `useSearchParams` (the pattern established by
 * DeviceSessionsForDay.tsx) and writes are always `replace: true` so
 * pan / brush / Jump-to don't flood browser history. Unknown range falls
 * back to the default silently.
 */
function useTimelineUrlState(): {
  rangeKey: RangeKey | 'custom';
  spanMs: number;
  centreMs: number | null;
  setRangeKey: (key: RangeKey) => void;
  setCentreMs: (next: number | null | ((c: number | null) => number | null)) => void;
  setCustomWindow: (centreMs: number, spanMs: number) => void;
  resetCustom: () => void;
} {
  const [params, setParams] = useSearchParams();
  const urlRange = params.get('range');
  const urlSpan = params.get('span');
  const urlUntil = params.get('until');

  let rangeKey: RangeKey | 'custom';
  let spanMs: number;
  const parsedSpan = urlSpan ? Number(urlSpan) : NaN;
  if (Number.isFinite(parsedSpan) && parsedSpan >= MIN_CUSTOM_SPAN_MS) {
    rangeKey = 'custom';
    spanMs = parsedSpan;
  } else {
    const preset = RANGES.find((r) => r.key === urlRange)?.key ?? DEFAULT_RANGE;
    rangeKey = preset;
    spanMs = spanForKey(preset);
  }
  const halfSpanMs = spanMs / 2;

  let centreMs: number | null = null;
  if (urlUntil) {
    const t = new Date(urlUntil).getTime();
    if (Number.isFinite(t)) centreMs = t - halfSpanMs;
  }

  function writeUrl(next: {
    rangeKey?: RangeKey | 'custom';
    spanMs?: number;
    centreMs?: number | null;
  }) {
    const nextRange = next.rangeKey ?? rangeKey;
    const nextSpan = next.spanMs ?? spanMs;
    const nextCentre = 'centreMs' in next ? (next.centreMs ?? null) : centreMs;

    const np = new URLSearchParams(params);
    if (nextRange === 'custom') {
      np.delete('range');
      np.set('span', String(Math.round(nextSpan)));
    } else {
      np.delete('span');
      if (nextRange === DEFAULT_RANGE) np.delete('range');
      else np.set('range', nextRange);
    }
    if (nextCentre === null) {
      np.delete('until');
    } else {
      np.set('until', new Date(nextCentre + nextSpan / 2).toISOString());
    }
    setParams(np, { replace: true });
  }

  return {
    rangeKey,
    spanMs,
    centreMs,
    setRangeKey: (key) => writeUrl({ rangeKey: key }),
    setCentreMs: (next) => {
      const resolved = typeof next === 'function' ? next(centreMs) : next;
      writeUrl({ centreMs: resolved });
    },
    setCustomWindow: (newCentre, newSpan) =>
      writeUrl({
        rangeKey: 'custom',
        spanMs: Math.max(newSpan, MIN_CUSTOM_SPAN_MS),
        centreMs: newCentre,
      }),
    resetCustom: () =>
      writeUrl({ rangeKey: nearestPresetTo(spanMs), spanMs: undefined }),
  };
}

/**
 * Optional snapshot of the device's *current* state, sourced from the AWS
 * shadow (`runtime.sys_state` / `runtime.demo_mode` / `projector.source`).
 * When this value differs from the last persisted event on a given lane,
 * the timeline replaces the trailing band with a "live" band painted in
 * the shadow's value and hatched, so the operator sees: we know the value
 * now, we just don't have the transition event. Pass `online: false` to
 * suppress reconciliation entirely — when the device is offline its
 * shadow may itself be stale (the offline overlay already conveys that).
 */
export interface LiveAnchor {
  sysState: number | null;
  demoMode: number | null;
  projSource: number | string | null;
  online: boolean;
  lastSeen: number | null; // ms epoch; absolute upper-bound for shadow freshness
}

export function DeviceStateTimeline({
  thing,
  liveAnchor,
}: {
  thing: string;
  liveAnchor?: LiveAnchor;
}) {
  // rangeKey + centreMs are URL-backed (range, until query params) so
  // reloading and shared links restore the same window. centreMs === null
  // means "live" — the window's right edge tracks nowMs. Pan ◀/▶ moves
  // the centre by half a window; ▶ clamps back to live when the centre
  // would put the right edge ≥ now. Centre-anchoring (vs the older
  // right-edge offset) makes range switches preserve the moment under
  // the cursor and is the natural output of brush-zoom / Jump-to.
  const {
    rangeKey,
    spanMs,
    centreMs,
    setRangeKey,
    setCentreMs,
    setCustomWindow,
    resetCustom,
  } = useTimelineUrlState();
  const isCustomSpan = rangeKey === 'custom';
  // For display: "custom" doesn't appear in RANGES, so fall back to a
  // synthetic range with the current spanMs so existing axis/range
  // formatting helpers keep working. The hours value drives axis-tick
  // density and the long-range year switch in fmtWindowEdge.
  const range = isCustomSpan
    ? { key: 'custom' as const, hours: spanMs / 3_600_000, label: 'custom range' }
    : RANGES.find((r) => r.key === rangeKey)!;
  // Per-dimension collapse state. Collapsed rows render as a 4 px coloured
  // strip with no in-row label (the chevron button in the 68 px label
  // column toggles them). Kept component-local on purpose — URL-encoding
  // every row toggle would add noise to shared links.
  const [collapsedRows, setCollapsedRows] = useState<Record<Dimension, boolean>>({
    sys_state: false,
    demo_mode: false,
    proj_source: false,
  });

  // Tooltip content snapshot. Re-set on every pointermove that crosses
  // into the chart. Position lives on refs (imperative DOM writes) so the
  // crosshair line and the HTML tooltip move at refresh-rate without
  // triggering React renders for every pixel.
  type HoverContent = {
    tMs: number;
    bandLabels: Record<Dimension, { label: string; color: string }>;
    nearestError: { ts: number; msg: string; deltaMs: number } | null;
    nearestBoot: { ts: number; fw: string; deltaMs: number } | null;
    // DE-160. Populated when tMs falls inside an offline span. Tooltip
    // shows "Device offline — since HH:mm (reason)" so the operator
    // doesn't read a stale state band as live truth.
    offline: { disconnectMs: number | null; reason: string | null } | null;
  };
  const [hover, setHover] = useState<HoverContent | null>(null);

  // Selected marker popover (T1.5). Holds either a single event or, when
  // markers are too dense to render individually (T2.5), every event in a
  // cluster. leftPx is the chosen anchor x (cluster centroid) relative to
  // the chart container — saves recomputing on every parent re-render.
  const [selectedMarker, setSelectedMarker] = useState<
    | { events: StateHistoryEvent[]; leftPx: number }
    | null
  >(null);

  // Esc dismisses the popover. Document-level so it works even if focus
  // is somewhere outside the chart.
  useEffect(() => {
    if (!selectedMarker) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedMarker(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedMarker]);

  // Measured chart-area width in pixels. The SVG draws in percent units
  // (viewBox 0..100, preserveAspectRatio="none"), but pointer hit-testing,
  // tooltip placement, brush rect, marker clustering, and min-pixel ticks
  // all need true pixels. ResizeObserver writes here whenever the chart
  // container resizes; downstream features skip their work while it's 0
  // so first paint isn't broken.
  const chartRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const crosshairRef = useRef<SVGLineElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const brushRectRef = useRef<SVGRectElement | null>(null);
  const jumpInputRef = useRef<HTMLInputElement | null>(null);

  // Brush state machine. `idle` → no pointer; `maybe-drag` → pointer is
  // down but hasn't moved beyond the click threshold (treat pointerup as
  // a click); `dragging` → moved past threshold, render the brush rect
  // and commit a custom window on pointerup.
  type DragState = 'idle' | 'maybe-drag' | 'dragging';
  const dragStateRef = useRef<DragState>('idle');
  const pointerDownXRef = useRef<number>(0);
  const [widthPx, setWidthPx] = useState(0);
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    setWidthPx(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidthPx(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Snap "now" to a round, sticky pair so refetches in the background don't
  // continuously shift the window and re-trigger fresh queries every render.
  // Bucket by minute — small drift only matters for the rightmost band.
  const nowMs = Math.floor(Date.now() / 60_000) * 60_000;
  const halfSpanMs = spanMs / 2;
  const isLive = centreMs === null;
  const untilMs = isLive ? nowMs : centreMs + halfSpanMs;
  const sinceMs = untilMs - spanMs;
  const sinceISO = new Date(sinceMs).toISOString();
  const untilISO = new Date(untilMs).toISOString();
  const stepMs = Math.max(halfSpanMs, 60_000);

  const q = useDeviceStateHistory(thing, sinceISO, untilISO);
  // Merge per-dim anchors (latest event of each type strictly before
  // `since`) ahead of the window events. Anchors carry ts < sinceMs;
  // buildBands / buildOfflineSpans clamp their effective band start to
  // sinceMs. Without anchors, narrowing the range past the last
  // transition would paint the leading edge as "unknown" — the chart
  // would forget what state the device was in 5 minutes ago just
  // because we zoomed in.
  const events = useMemo<StateHistoryEvent[]>(() => {
    const window = q.data?.events ?? [];
    const anchors = q.data?.anchors ?? [];
    if (anchors.length === 0) return window;
    return [...anchors, ...window];
  }, [q.data]);

  // reconcileWithLive is defined at module scope (pure + unit-testable).

  // DE-160. Offline intervals. Presence-derived spans (MQTT disconnect →
  // connect) plus a synthetic trailing span when the `online` pill says the
  // device is offline (stale last_seen) but no presence disconnect was
  // recorded — keeps the timeline in step with the pill instead of waiting
  // for the presence scrubber (scrub_stale_presence in schema.sql). Computed
  // before rowBands so the lane bands can be blanked inside offline spans.
  const offlineSpans = useMemo(
    () =>
      withSyntheticOffline(
        buildOfflineSpans(events, sinceMs, untilMs),
        { online: liveAnchor?.online ?? true, lastSeen: liveAnchor?.lastSeen ?? null },
        sinceMs,
        untilMs,
      ),
    [events, sinceMs, untilMs, liveAnchor?.online, liveAnchor?.lastSeen],
  );

  // Memoise the per-dimension band derivations so pointer-move (which only
  // writes refs, never state) doesn't accidentally trip an O(n) rebuild on
  // wide ranges. Pipeline per lane: buildBands (incl. boot resets) →
  // reconcile against the live shadow → mask Demo/Source during Standby →
  // mask the 'Off' source → blank everything inside offline spans (so stale
  // bands don't bleed through the offline overlay).
  const rowBands: Record<Dimension, Band[]> = useMemo(() => {
    const sysRaw = buildBands(events, 'sys_state', sinceMs, untilMs);
    const demoRaw = buildBands(events, 'demo_mode', sinceMs, untilMs);
    const srcRaw = buildBands(events, 'proj_source', sinceMs, untilMs);
    // Reconcile against the live shadow BEFORE the Standby mask, so the mask
    // sees the device's current sys_state (e.g. LampOn) rather than a stale
    // Standby band. NB: only the Source lane is masked during Standby — the
    // Demo lane shows its real status (e.g. "supported") in Standby too, since
    // demo capability/state stays meaningful while the projector is off.
    const sys = reconcileWithLive(sysRaw, 'sys_state', liveAnchor?.sysState ?? null, liveAnchor, nowMs);
    const demo = reconcileWithLive(demoRaw, 'demo_mode', liveAnchor?.demoMode ?? null, liveAnchor, nowMs);
    const src = reconcileWithLive(
      srcRaw,
      'proj_source',
      canonSource(liveAnchor?.projSource ?? null),
      liveAnchor,
      nowMs,
    );
    const isStandby = (b: Band) => b.value === 0;
    // 'Off' is the literal label the projector reports when it can't identify
    // a source (input pipeline dark for reasons unrelated to system state) —
    // mask it so the row reads as "no source coverage" in those intervals.
    const isOffSource = (b: Band) => b.value === 'Off';
    // Offline spans blank ALL lanes (applied last) so the offline overlay
    // reads as a clean block with no stale colour beneath it.
    const offlineMasks: Band[] = offlineSpans.map((s) => ({
      start: s.start,
      end: s.end,
      value: null,
      label: '',
    }));
    const maskOffline = (bands: Band[]) =>
      offlineMasks.length ? maskBandsAgainst(bands, offlineMasks, () => true) : bands;
    return {
      sys_state: maskOffline(sys),
      // Demo is NOT Standby-masked: show its real status (supported/active/…)
      // even while the projector is in Standby.
      demo_mode: maskOffline(demo),
      proj_source: maskOffline(
        maskBandsAgainst(maskBandsAgainst(src, sys, isStandby), src, isOffSource),
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- module-level pure
    // helpers (buildBands/reconcileWithLive/maskBandsAgainst); inputs listed below.
  }, [
    events,
    sinceMs,
    untilMs,
    nowMs,
    offlineSpans,
    liveAnchor?.online,
    liveAnchor?.sysState,
    liveAnchor?.demoMode,
    liveAnchor?.projSource,
    liveAnchor?.lastSeen,
  ]);

  // In-window only: a pre-window `boot` anchor (added to the state-history
  // anchors so buildBands can reset the leading edge across a reboot) must
  // not render as an off-screen tick.
  const errorMarkers = useMemo(
    () =>
      events.filter((e) => {
        if (e.event_type !== 'error') return false;
        const t = new Date(e.ts).getTime();
        return t >= sinceMs && t <= untilMs;
      }),
    [events, sinceMs, untilMs],
  );
  const bootMarkers = useMemo(
    () =>
      events.filter((e) => {
        if (e.event_type !== 'boot') return false;
        const t = new Date(e.ts).getTime();
        return t >= sinceMs && t <= untilMs;
      }),
    [events, sinceMs, untilMs],
  );

  // Marker clustering (T2.5). When two markers would render within ~3 px
  // of each other we merge them into a single cluster anchored at the
  // mean timestamp. Errors and boots cluster independently — they render
  // in different styles, so combining them would be misleading. Skipped
  // while widthPx is 0 (first paint) so the chart renders something
  // sensible immediately.
  const errorClusters = useMemo(
    () => clusterMarkers(errorMarkers, spanMs, widthPx),
    [errorMarkers, spanMs, widthPx],
  );
  const bootClusters = useMemo(
    () => clusterMarkers(bootMarkers, spanMs, widthPx),
    [bootMarkers, spanMs, widthPx],
  );

  const span = Math.max(untilMs - sinceMs, 1);
  const xOf = (ms: number) => ((ms - sinceMs) / span) * 100; // percent

  // Axis ticks: five evenly-spaced labels across the window.
  const axisTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    pct: t * 100,
    label: fmtAxisTick(sinceMs + t * span, range.hours),
  }));

  // Per-row metrics: height + cumulative y. Collapsed rows shrink the band
  // strip to a 4 px sliver but keep the label-column row at the same height
  // so the CSS grid stays aligned with the SVG. Markers always span the
  // full chart height regardless of which rows are collapsed.
  const rowMetrics = (() => {
    let yAcc = 0;
    return ROW_LABELS.map((r, i) => {
      const height = collapsedRows[r.dim] ? ROW_HEIGHT_COLLAPSED : ROW_HEIGHT_EXPANDED;
      const y = yAcc;
      yAcc += height + (i < ROW_LABELS.length - 1 ? ROW_GAP : 0);
      return { ...r, height, y };
    });
  })();
  const totalHeight =
    rowMetrics.reduce((sum, m) => sum + m.height, 0) +
    (ROW_LABELS.length - 1) * ROW_GAP;

  // Legend swatches for the colours actually present in the window — one
  // row per timeline so the eye maps each color back to its dimension
  // without ambiguity (used to be one flex-wrapped row mixing all three).
  // Computes per-dimension; each row is independent and may be empty.
  const legendPerDim = useMemo<
    Record<Dimension, Array<{ key: string; label: string; color: string }>>
  >(() => {
    const acc: Record<Dimension, Array<{ key: string; label: string; color: string }>> = {
      sys_state: [],
      demo_mode: [],
      proj_source: [],
    };
    for (const dim of ['sys_state', 'demo_mode', 'proj_source'] as Dimension[]) {
      const seen = new Set<string>();
      for (const b of rowBands[dim]) {
        if (b.value !== null) {
          const k = `${dim}:${String(b.value)}`;
          if (!seen.has(k)) {
            seen.add(k);
            acc[dim].push({
              key: k,
              label: labelFor(dim, b.value),
              color: colorFor(dim, b.value),
            });
          }
        }
      }
    }
    return acc;
  }, [rowBands]);
  const anyLegendRows =
    legendPerDim.sys_state.length > 0 ||
    legendPerDim.demo_mode.length > 0 ||
    legendPerDim.proj_source.length > 0 ||
    errorMarkers.length > 0 ||
    bootMarkers.length > 0 ||
    offlineSpans.length > 0;

  // Pointer interactions. The capture overlay is a transparent <div> over
  // the chart area; both crosshair (SVG) and tooltip (HTML) move via refs
  // so per-pixel pointer-move never triggers a React render. Only the
  // tooltip's textual *content* (band labels + nearest markers) goes
  // through state — that's a small subtree and changes at most once per
  // pixel, which React's reconciler handles cheaply over the memoised
  // band arrays. Brush selection is similarly ref-driven; React state
  // changes only on pointerup (commit) or click (band focus / marker).
  const DRAG_THRESHOLD_PX = 3;

  const showCrosshair = (pct: number, x: number, chartWidthPx: number) => {
    if (crosshairRef.current) {
      const cx = String(pct * 100);
      crosshairRef.current.setAttribute('x1', cx);
      crosshairRef.current.setAttribute('x2', cx);
      crosshairRef.current.style.visibility = 'visible';
    }
    if (tooltipRef.current) {
      tooltipRef.current.style.visibility = 'visible';
      // The tooltip uses `transform: translateX(-50%)` so style.left places
      // its centre at x. Clamp so the visible box stays inside the chart
      // even when the cursor is near either edge.
      const halfW = tooltipRef.current.offsetWidth / 2;
      const clampedX =
        halfW > 0
          ? Math.max(halfW, Math.min(chartWidthPx - halfW, x))
          : x;
      tooltipRef.current.style.left = `${clampedX}px`;
    }
  };

  const hideCrosshair = () => {
    if (crosshairRef.current) crosshairRef.current.style.visibility = 'hidden';
    if (tooltipRef.current) tooltipRef.current.style.visibility = 'hidden';
  };

  const showBrush = (downPct: number, currPct: number) => {
    if (!brushRectRef.current) return;
    const left = Math.min(downPct, currPct) * 100;
    const width = Math.abs(currPct - downPct) * 100;
    brushRectRef.current.setAttribute('x', String(left));
    brushRectRef.current.setAttribute('width', String(width));
    brushRectRef.current.style.visibility = 'visible';
  };

  const hideBrush = () => {
    if (brushRectRef.current) brushRectRef.current.style.visibility = 'hidden';
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const x = e.clientX - rect.left;
    pointerDownXRef.current = x;
    dragStateRef.current = 'maybe-drag';
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));

    // Branch on drag state. While dragging, only the brush rect updates —
    // we keep the tooltip hidden so the user's focus is on the selection.
    if (dragStateRef.current === 'dragging') {
      const downPct = pointerDownXRef.current / rect.width;
      showBrush(downPct, pct);
      return;
    }
    if (dragStateRef.current === 'maybe-drag') {
      if (Math.abs(x - pointerDownXRef.current) >= DRAG_THRESHOLD_PX) {
        dragStateRef.current = 'dragging';
        hideCrosshair();
        const downPct = pointerDownXRef.current / rect.width;
        showBrush(downPct, pct);
        return;
      }
      // Still under threshold — fall through and keep crosshair + tooltip
      // current so a tiny pointer wobble before a click still updates the
      // read-out.
    }

    showCrosshair(pct, x, rect.width);
    const tMs = sinceMs + pct * spanMs;
    const tolMs = Math.max(spanMs * 0.01, 30_000);
    const bandLabels: Record<Dimension, { label: string; color: string }> = {
      sys_state: { label: '', color: '' },
      demo_mode: { label: '', color: '' },
      proj_source: { label: '', color: '' },
    };
    // The Source lane is masked during Standby (projector off ⇒ no meaningful
    // source); give it a "n/a in standby" read-out instead of "unknown". The
    // Demo lane is NOT masked in Standby, so it shows its real value there.
    const sysBandAtT = rowBands.sys_state.find((b) => tMs >= b.start && tMs < b.end);
    const inStandby = sysBandAtT?.value === 0;
    for (const dim of ['sys_state', 'demo_mode', 'proj_source'] as Dimension[]) {
      const band = rowBands[dim].find((b) => tMs >= b.start && tMs < b.end);
      const value: BandValue = band?.value ?? null;
      if (dim === 'proj_source' && inStandby && band === undefined) {
        bandLabels[dim] = { label: '— (standby)', color: colorFor('sys_state', 0) };
      } else {
        bandLabels[dim] = { label: labelFor(dim, value), color: colorFor(dim, value) };
      }
    }
    let nearestErrEv: StateHistoryEvent | null = null;
    let nearestErrDelta = Infinity;
    for (const ev of errorMarkers) {
      const d = Math.abs(new Date(ev.ts).getTime() - tMs);
      if (d < nearestErrDelta && d <= tolMs) {
        nearestErrDelta = d;
        nearestErrEv = ev;
      }
    }
    let nearestBootEv: StateHistoryEvent | null = null;
    let nearestBootDelta = Infinity;
    for (const ev of bootMarkers) {
      const d = Math.abs(new Date(ev.ts).getTime() - tMs);
      if (d < nearestBootDelta && d <= tolMs) {
        nearestBootDelta = d;
        nearestBootEv = ev;
      }
    }
    const offlineHit = offlineSpans.find((s) => tMs >= s.start && tMs < s.end) ?? null;
    setHover({
      tMs,
      bandLabels,
      nearestError: nearestErrEv
        ? {
            ts: new Date(nearestErrEv.ts).getTime(),
            msg: String(
              (nearestErrEv.data as { message?: unknown }).message ??
                (nearestErrEv.data as { code?: unknown }).code ??
                'error',
            ),
            deltaMs: nearestErrDelta,
          }
        : null,
      nearestBoot: nearestBootEv
        ? {
            ts: new Date(nearestBootEv.ts).getTime(),
            fw: String((nearestBootEv.data as { fw_ver?: unknown }).fw_ver ?? '?'),
            deltaMs: nearestBootDelta,
          }
        : null,
      offline: offlineHit
        ? { disconnectMs: offlineHit.disconnectMs, reason: offlineHit.reason }
        : null,
    });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) {
      dragStateRef.current = 'idle';
      return;
    }
    const upX = e.clientX - rect.left;
    const wasDrag = dragStateRef.current === 'dragging';
    dragStateRef.current = 'idle';
    e.currentTarget.releasePointerCapture(e.pointerId);

    if (wasDrag) {
      // Commit a brushed window. Convert px → time, clamp inside the
      // current window, enforce a 5-minute minimum so a fat-fingered
      // brush doesn't collapse the view to nothing.
      const downX = pointerDownXRef.current;
      const x0 = Math.min(downX, upX);
      const x1 = Math.max(downX, upX);
      const t0 = sinceMs + (x0 / rect.width) * spanMs;
      const t1 = sinceMs + (x1 / rect.width) * spanMs;
      const newCentre = (t0 + t1) / 2;
      const newSpan = Math.max(t1 - t0, MIN_CUSTOM_SPAN_MS);
      hideBrush();
      setCustomWindow(newCentre, newSpan);
      return;
    }

    // Click path (drag distance < threshold). Marker hit-test runs first
    // (markers are visually thin; we widen the hit zone in px-space so
    // they're easy to click); on miss, fall back to band hit-test for
    // click-to-focus. Existing marker popover acts as a click sponge: any
    // click off a marker dismisses it instead of triggering band focus.
    const pct = Math.max(0, Math.min(1, upX / rect.width));
    const tMs = sinceMs + pct * spanMs;

    const MARKER_TOL_PX = 5;
    const markerTolMs = (MARKER_TOL_PX / rect.width) * spanMs;
    // Hit-test against rendered *clusters*, not individual events, so
    // long-range clicks on a stacked-marker spot open the whole stack at
    // once. The popover then shows the cluster's events as a list.
    let nearestCluster: MarkerCluster | null = null;
    let nearestClusterDist = Infinity;
    for (const c of errorClusters) {
      const d = Math.abs(c.centroidMs - tMs);
      if (d < nearestClusterDist && d <= markerTolMs) {
        nearestClusterDist = d;
        nearestCluster = c;
      }
    }
    for (const c of bootClusters) {
      const d = Math.abs(c.centroidMs - tMs);
      if (d < nearestClusterDist && d <= markerTolMs) {
        nearestClusterDist = d;
        nearestCluster = c;
      }
    }
    if (nearestCluster) {
      const evPct = (nearestCluster.centroidMs - sinceMs) / spanMs;
      setSelectedMarker({
        events: nearestCluster.events,
        leftPx: evPct * rect.width,
      });
      return;
    }
    if (selectedMarker) {
      // First click off the popover dismisses it; don't also focus a band
      // — that would be two surprises from a single click.
      setSelectedMarker(null);
      return;
    }

    const y = e.clientY - rect.top;
    const hitRow = rowMetrics.find((r) => y >= r.y && y < r.y + r.height);
    if (!hitRow) return;
    const band = rowBands[hitRow.dim].find((b) => tMs >= b.start && tMs < b.end);
    if (!band) return;
    const bandSpan = band.end - band.start;
    const pad = bandSpan * 0.1;
    const focusCentre = (band.start + band.end) / 2;
    let focusSpan = Math.max(bandSpan + 2 * pad, MIN_CUSTOM_SPAN_MS);
    if (focusCentre + focusSpan / 2 > nowMs) {
      focusSpan = Math.max((nowMs - focusCentre) * 2, MIN_CUSTOM_SPAN_MS);
    }
    setCustomWindow(focusCentre, focusSpan);
  };

  const handlePointerLeave = () => {
    if (dragStateRef.current === 'dragging') {
      // Pointer left mid-brush — abort the selection rather than commit a
      // window the user can't see the right edge of.
      hideBrush();
    }
    dragStateRef.current = 'idle';
    hideCrosshair();
    setHover(null);
  };

  return (
    <section className="card">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="font-medium">System status timeline</h2>
          <p className="text-[10px] text-muted tabular-nums">
            {fmtWindowEdge(sinceMs, range.hours)}
            {' → '}
            {fmtWindowEdge(untilMs, range.hours)}
            <span className="mx-1.5">·</span>
            {range.label}
            <span className="mx-1.5">·</span>
            {events.length} events
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Slide controls — page the window backwards / forwards by
              half-window steps. `live` indicator doubles as the reset
              button when paged back. ▶ disabled at offset=0. */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() =>
                setCentreMs((c) => (c === null ? nowMs - halfSpanMs : c) - stepMs)
              }
              className="px-2 py-1 rounded-md text-xs font-medium bg-soft text-muted hover:text-fg"
              title={`Step back ${Math.round(stepMs / 60_000)} min`}
              aria-label="Earlier"
            >
              ◀
            </button>
            {isLive ? (
              <span
                className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted tabular-nums"
                title="Window ends at now"
              >
                live
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setCentreMs(null)}
                className="px-2 py-1 rounded-md text-[10px] uppercase tracking-wide text-accent hover:underline tabular-nums"
                title="Snap window back to now"
              >
                {fmtOffsetAgo(nowMs - untilMs)} ago · now
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                setCentreMs((c) => {
                  if (c === null) return null;
                  const next = c + stepMs;
                  return next + halfSpanMs >= nowMs ? null : next;
                })
              }
              disabled={isLive}
              className="px-2 py-1 rounded-md text-xs font-medium bg-soft text-muted hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed"
              title={`Step forward ${Math.round(stepMs / 60_000)} min`}
              aria-label="Later"
            >
              ▶
            </button>
            {/* Jump-to datetime picker. The native <input type="datetime-local">
                is overlaid invisibly on top of the 📅 chip — clicking the
                chip hits the input directly, so the browser opens its own
                picker and (critically) anchors the click-outside / Escape
                dismiss logic to the visible chip area. Using `sr-only`
                here used to leave the picker anchored to a 1×1 clipped
                box somewhere offscreen, which broke close-on-click-outside
                in some browsers. */}
            <span
              className="relative inline-block"
              title="Jump to a specific date and time"
            >
              <button
                type="button"
                tabIndex={-1}
                aria-hidden="true"
                className="px-2 py-1 rounded-md text-xs font-medium bg-soft text-muted hover:text-fg pointer-events-none"
              >
                📅
              </button>
              <input
                ref={jumpInputRef}
                type="datetime-local"
                step={60}
                max={toLocalDatetimeInputValue(nowMs)}
                value={toLocalDatetimeInputValue(untilMs)}
                aria-label="Jump to date and time"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  const t = new Date(v).getTime();
                  if (!Number.isFinite(t)) return;
                  const clamped = Math.min(t, nowMs);
                  if (clamped >= nowMs - 60_000) {
                    setCentreMs(null);
                  } else {
                    setCentreMs(clamped - halfSpanMs);
                  }
                }}
              />
            </span>
          </div>
          <div className="flex gap-1 items-center">
            {isCustomSpan && (
              <button
                type="button"
                onClick={resetCustom}
                className="px-2 py-1 rounded-md text-xs font-medium bg-warn-soft text-warn hover:bg-warn hover:text-white"
                title="Exit brush-zoom — snap to nearest preset range"
              >
                ↺ Reset
              </button>
            )}
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRangeKey(r.key)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                  rangeKey === r.key ? 'bg-accent text-white' : 'bg-soft text-muted hover:text-fg'
                }`}
                title={r.label}
              >
                {r.key}
              </button>
            ))}
          </div>
        </div>
      </div>

      {q.isLoading && <p className="text-sm text-muted">Loading…</p>}
      {!q.isLoading && events.length === 0 && (
        <p className="text-sm text-muted">
          No state transitions in this window. Try a wider range, or wait for the device
          to publish a state-change event.
        </p>
      )}

      {!q.isLoading && (
        <div className="space-y-2">
          {/* Row labels + chart area share a CSS grid so labels stay aligned
              with their corresponding band rows regardless of viewport. */}
          <div className="grid grid-cols-[68px_1fr] gap-2 items-stretch">
            <div className="flex flex-col gap-[6px] text-[11px] text-muted">
              {rowMetrics.map((r) => {
                const collapsed = collapsedRows[r.dim];
                return (
                  <button
                    key={r.dim}
                    type="button"
                    onClick={() =>
                      setCollapsedRows((s) => ({ ...s, [r.dim]: !s[r.dim] }))
                    }
                    style={{ height: `${r.height}px`, lineHeight: 1 }}
                    className="flex items-center gap-1 text-left whitespace-nowrap hover:text-fg overflow-visible"
                    title={collapsed ? `Expand ${r.label} row` : `Collapse ${r.label} row`}
                    aria-expanded={!collapsed}
                  >
                    <span aria-hidden="true" className="inline-block w-3 text-center">
                      {collapsed ? '▸' : '▾'}
                    </span>
                    {!collapsed && <span>{r.label}</span>}
                  </button>
                );
              })}
            </div>
            <div ref={chartRef} className="relative">
              <svg
                width="100%"
                height={totalHeight}
                viewBox={`0 0 100 ${totalHeight}`}
                preserveAspectRatio="none"
                role="img"
                aria-label="Device state timeline"
              >
                {rowMetrics.map((r) => {
                  const bands = rowBands[r.dim];
                  return (
                    <g key={r.dim}>
                      {/* Background row stripe — clarifies which row is which
                          when there are gaps between bands. */}
                      <rect
                        x={0}
                        y={r.y}
                        width={100}
                        height={r.height}
                        fill="#f8fafc"
                        rx={2}
                      />
                      {bands.map((b, i) => {
                        const left = xOf(b.start);
                        const right = xOf(b.end);
                        const width = Math.max(right - left, 0.15);
                        return (
                          <rect
                            key={i}
                            x={left}
                            y={r.y}
                            width={width}
                            height={r.height}
                            fill={colorFor(r.dim, b.value)}
                            opacity={b.value === null ? 0.4 : 0.85}
                          />
                        );
                      })}
                    </g>
                  );
                })}

                {/* Error markers — render last so they sit on top of bands.
                    Clustered into single ticks at long ranges so dense
                    error storms don't smear into one opaque red bar; a
                    count badge appears for clusters with >1 event. */}
                {errorClusters.map((c, i) => {
                  const x = xOf(c.centroidMs);
                  return (
                    <g key={`err-${i}`}>
                      <line
                        x1={x}
                        x2={x}
                        y1={0}
                        y2={totalHeight}
                        stroke="#dc2626"
                        strokeWidth={0.25}
                        vectorEffect="non-scaling-stroke"
                      />
                      {c.events.length > 1 && (
                        <text
                          x={x}
                          y={-2}
                          textAnchor="middle"
                          fill="#dc2626"
                          fontSize={6}
                          fontFamily="ui-sans-serif, system-ui"
                          style={{ paintOrder: 'stroke' }}
                          stroke="#fff"
                          strokeWidth={1.5}
                        >
                          ×{c.events.length}
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* Boot markers — dashed teal verticals (clustered). */}
                {bootClusters.map((c, i) => {
                  const x = xOf(c.centroidMs);
                  return (
                    <g key={`boot-${i}`}>
                      <line
                        x1={x}
                        x2={x}
                        y1={0}
                        y2={totalHeight}
                        stroke="#0d9488"
                        strokeWidth={0.18}
                        strokeDasharray="2 2"
                        vectorEffect="non-scaling-stroke"
                      />
                      {c.events.length > 1 && (
                        <text
                          x={x}
                          y={totalHeight + 8}
                          textAnchor="middle"
                          fill="#0d9488"
                          fontSize={6}
                          fontFamily="ui-sans-serif, system-ui"
                          style={{ paintOrder: 'stroke' }}
                          stroke="#fff"
                          strokeWidth={1.5}
                        >
                          ×{c.events.length}
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* Crosshair — moves imperatively via crosshairRef on
                    pointermove. visibility=hidden by default; pointermove
                    sets it visible. Rendered last so it sits on top of
                    bands and markers. */}
                <line
                  ref={crosshairRef}
                  x1={-10}
                  x2={-10}
                  y1={0}
                  y2={totalHeight}
                  stroke="#1C1B1F"
                  strokeWidth={0.3}
                  vectorEffect="non-scaling-stroke"
                  style={{ visibility: 'hidden', pointerEvents: 'none' }}
                />
                {/* Brush rect — moves imperatively via brushRectRef during
                    a drag selection. Translucent accent fill with a thin
                    border. Hidden until pointer-move crosses the click
                    threshold. */}
                <rect
                  ref={brushRectRef}
                  x={0}
                  y={0}
                  width={0}
                  height={totalHeight}
                  fill="#00B4D0"
                  fillOpacity={0.18}
                  stroke="#00B4D0"
                  strokeWidth={0.4}
                  vectorEffect="non-scaling-stroke"
                  style={{ visibility: 'hidden', pointerEvents: 'none' }}
                />
              </svg>

              {/* DE-160. Offline overlay — one hatched stripe per offline
                  span (presence-derived, plus a synthetic span when the
                  online pill reads offline with no recorded disconnect),
                  covering the full chart height. The lane bands are blanked
                  inside offline spans (see rowBands) so this reads as a
                  clean block. pointer-events:none so brush + crosshair
                  still work through the overlay. */}
              {offlineSpans.length > 0 && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  aria-hidden="true"
                >
                  {offlineSpans.map((s, i) => (
                    <div
                      key={`offline-${i}`}
                      className="absolute"
                      style={{
                        left: `${((s.start - sinceMs) / spanMs) * 100}%`,
                        width: `${((s.end - s.start) / spanMs) * 100}%`,
                        top: 0,
                        height: totalHeight,
                        background: OFFLINE_HATCH_BG,
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Sub-pixel "presence" ticks (T2.6). At long ranges some
                  bands compute to a width < 2 px, which the SVG
                  faithfully renders as a hairline that's effectively
                  invisible and unhoverable. We overlay a 2 px tick at
                  each such band's left edge so brief state blips remain
                  discoverable. We never *widen* the underlying SVG rect
                  — adjacent bands would visibly overlap. */}
              {widthPx > 0 && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  aria-hidden="true"
                >
                  {rowMetrics.flatMap((r) =>
                    rowBands[r.dim]
                      .filter(
                        (b) =>
                          b.value !== null &&
                          ((b.end - b.start) / spanMs) * widthPx < 2,
                      )
                      .map((b, i) => (
                        <div
                          key={`tick-${r.dim}-${i}`}
                          className="absolute"
                          style={{
                            left: `${((b.start - sinceMs) / spanMs) * widthPx}px`,
                            top: r.y,
                            width: 2,
                            height: r.height,
                            backgroundColor: colorFor(r.dim, b.value),
                            opacity: 0.85,
                          }}
                        />
                      )),
                  )}
                </div>
              )}

              {/* Transparent capture overlay — owns all pointer interaction
                  for crosshair / tooltip / brush / click. aria-hidden
                  because the SVG itself already exposes role="img". */}
              <div
                ref={overlayRef}
                aria-hidden="true"
                className="absolute inset-0 cursor-crosshair"
                style={{ touchAction: 'none' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerLeave}
              />

              {/* Marker click popover — opens when handlePointerUp's
                  marker hit-test finds an error/boot within ~5 px. Stays
                  open until Esc, "×", a "View in Recent events" click,
                  or any subsequent click on the chart. */}
              {selectedMarker && (() => {
                const firstEv = selectedMarker.events[0]!;
                const isCluster = selectedMarker.events.length > 1;
                const scrollToEvent = (eventId: string) => {
                  const el = document.getElementById(`event-${eventId}`);
                  if (!el) return;
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.classList.add('!bg-accent-soft', 'transition-colors');
                  window.setTimeout(
                    () => el.classList.remove('!bg-accent-soft'),
                    1500,
                  );
                };
                // Flip the popover below the chart when there isn't room
                // above (chart sits high in the viewport). 240 is the
                // popover's worst-case rendered height — easier than
                // measuring on the first render and avoids a layout pass.
                const chartTop =
                  chartRef.current?.getBoundingClientRect().top ?? 0;
                const flipBelow = chartTop < 240;
                return (
                  <div
                    role="dialog"
                    aria-label={`${firstEv.event_type} details`}
                    className={`absolute z-30 -translate-x-1/2 ${
                      flipBelow ? '' : '-translate-y-full'
                    } bg-white border border-border rounded-md shadow-xl text-[11px] text-fg p-3 min-w-[220px] max-w-[340px]`}
                    style={{
                      top: flipBelow ? totalHeight + 8 : -8,
                      left: selectedMarker.leftPx,
                    }}
                  >
                    <div className="flex items-baseline justify-between mb-1 gap-2">
                      <div className="font-medium capitalize">
                        {firstEv.event_type}
                        {isCluster && (
                          <span className="ml-1.5 text-muted font-normal">
                            (×{selectedMarker.events.length} clustered)
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedMarker(null)}
                        className="text-muted hover:text-fg leading-none -mr-1 px-1"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    {!isCluster ? (
                      <>
                        <div className="text-muted tabular-nums mb-1.5">
                          {fmtWindowEdge(
                            new Date(firstEv.ts).getTime(),
                            range.hours,
                          )}
                        </div>
                        <pre className="text-[10px] font-mono bg-soft rounded px-2 py-1 max-h-32 overflow-auto whitespace-pre-wrap break-all">
                          {JSON.stringify(firstEv.data, null, 2)}
                        </pre>
                        <button
                          type="button"
                          onClick={() => {
                            scrollToEvent(firstEv.event_id);
                            setSelectedMarker(null);
                          }}
                          className="mt-2 text-accent hover:underline text-[10px]"
                        >
                          View in Recent events ↓
                        </button>
                      </>
                    ) : (
                      <ul className="space-y-1 max-h-48 overflow-auto -mx-1 px-1">
                        {selectedMarker.events.map((ev) => (
                          <li
                            key={ev.event_id}
                            className="flex items-baseline gap-2"
                          >
                            <span className="text-muted tabular-nums shrink-0">
                              {new Date(ev.ts).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                scrollToEvent(ev.event_id);
                                setSelectedMarker(null);
                              }}
                              className="text-accent hover:underline text-left truncate"
                              title="View in Recent events ↓"
                            >
                              {(() => {
                                if (ev.event_type === 'error') {
                                  const d = ev.data as {
                                    message?: unknown;
                                    code?: unknown;
                                  };
                                  return String(d.message ?? d.code ?? 'error');
                                }
                                if (ev.event_type === 'boot') {
                                  const d = ev.data as { fw_ver?: unknown };
                                  return `fw ${String(d.fw_ver ?? '?')}`;
                                }
                                return JSON.stringify(ev.data);
                              })()}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })()}

              {/* Styled HTML tooltip. Position via ref (style.left); content
                  via state (`hover`). pointer-events: none so it can't
                  steal hover from the overlay underneath. */}
              <div
                ref={tooltipRef}
                role="tooltip"
                className="absolute z-20 pointer-events-none -translate-x-1/2 -translate-y-full bg-white border border-border rounded-md shadow-lg px-2.5 py-1.5 text-[10px] text-fg tabular-nums whitespace-nowrap"
                style={{ visibility: 'hidden', top: -4, left: 0 }}
              >
                {hover && (
                  <>
                    <div className="font-medium mb-1">
                      {fmtWindowEdge(hover.tMs, range.hours)}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {(['sys_state', 'demo_mode', 'proj_source'] as Dimension[]).map((dim) => {
                        const e = hover.bandLabels[dim];
                        const label = ROW_LABELS.find((r) => r.dim === dim)!.label;
                        return (
                          <div key={dim} className="flex items-center gap-1.5">
                            <span
                              className="inline-block w-2 h-2 rounded-sm"
                              style={{ backgroundColor: e.color }}
                            />
                            <span className="text-muted">{label}:</span>
                            <span>{e.label}</span>
                          </div>
                        );
                      })}
                      {hover.nearestError && (
                        <div className="flex items-center gap-1.5 pt-1 mt-0.5 border-t border-border">
                          <span className="inline-block w-[2px] h-2.5 bg-red-600" />
                          <span className="text-muted">
                            error {fmtOffsetAgo(hover.nearestError.deltaMs)}{' '}
                            {hover.nearestError.ts <= hover.tMs ? 'ago' : 'ahead'}:
                          </span>
                          <span className="truncate max-w-[24ch]">
                            {hover.nearestError.msg}
                          </span>
                        </div>
                      )}
                      {hover.nearestBoot && (
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block w-[2px] h-2.5"
                            style={{
                              background:
                                'repeating-linear-gradient(to bottom,#0d9488 0 2px,transparent 2px 4px)',
                            }}
                          />
                          <span className="text-muted">
                            boot {fmtOffsetAgo(hover.nearestBoot.deltaMs)}{' '}
                            {hover.nearestBoot.ts <= hover.tMs ? 'ago' : 'ahead'}:
                          </span>
                          <span>fw {hover.nearestBoot.fw}</span>
                        </div>
                      )}
                      {hover.offline && (
                        <div className="flex items-center gap-1.5 pt-1 mt-0.5 border-t border-border">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-sm"
                            style={{ background: OFFLINE_HATCH_BG }}
                          />
                          <span className="text-muted">
                            offline
                            {hover.offline.disconnectMs !== null
                              ? ` since ${new Date(hover.offline.disconnectMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                              : ''}
                            {hover.offline.reason ? ` (${hover.offline.reason})` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* X-axis tick labels — share the same grid columns as the chart
              so they line up against the SVG's percentage coordinates. */}
          <div className="grid grid-cols-[68px_1fr] gap-2">
            <div />
            <div className="relative h-4 text-[10px] text-muted">
              {axisTicks.map((t, i) => (
                <span
                  key={i}
                  className="absolute tabular-nums"
                  style={{
                    left: `${t.pct}%`,
                    transform:
                      i === 0
                        ? 'translateX(0%)'
                        : i === axisTicks.length - 1
                          ? 'translateX(-100%)'
                          : 'translateX(-50%)',
                  }}
                >
                  {t.label}
                </span>
              ))}
            </div>
          </div>

          {anyLegendRows && (
            <div className="pt-1 border-t border-border space-y-1">
              {/* One legend row per timeline — same 68px label column as
                  the chart so colours align under their corresponding row.
                  Empty dims still render the label + a muted "—" so the
                  reader knows that row had no transitions in this window. */}
              {ROW_LABELS.map((r) => {
                const items = legendPerDim[r.dim];
                return (
                  <div
                    key={r.dim}
                    className="grid grid-cols-[68px_1fr] gap-2 text-[10px] text-muted"
                  >
                    <div className="font-medium">{r.label}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {items.length === 0 ? (
                        <span className="text-muted/60">—</span>
                      ) : (
                        items.map((l) => (
                          <span key={l.key} className="inline-flex items-center gap-1">
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-sm"
                              style={{ backgroundColor: l.color }}
                            />
                            {l.label}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Markers (error / boot / offline) + utility link — fourth
                  row so it doesn't crowd the per-dimension legends above. */}
              {(errorMarkers.length > 0 ||
                bootMarkers.length > 0 ||
                offlineSpans.length > 0 ||
                events.length > 0) && (
                <div className="grid grid-cols-[68px_1fr] gap-2 text-[10px] text-muted">
                  <div className="font-medium">Markers</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {errorMarkers.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-[2px] h-3 bg-red-600" />
                        error ({errorMarkers.length})
                      </span>
                    )}
                    {bootMarkers.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span
                          className="inline-block w-[2px] h-3"
                          style={{
                            background:
                              'repeating-linear-gradient(to bottom,#0d9488 0 2px,transparent 2px 4px)',
                          }}
                        />
                        boot ({bootMarkers.length})
                      </span>
                    )}
                    {offlineSpans.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span
                          className="inline-block w-3 h-3 rounded-sm"
                          style={{ background: OFFLINE_HATCH_BG }}
                        />
                        offline ({offlineSpans.length})
                      </span>
                    )}
                    {errorMarkers.length === 0 &&
                      bootMarkers.length === 0 &&
                      offlineSpans.length === 0 && (
                        <span className="text-muted/60">—</span>
                      )}
                    <button
                      type="button"
                      onClick={() => {
                        document
                          .getElementById('recent-events')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="ml-auto text-accent hover:underline"
                    >
                      see recent events ↓
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
